import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import FreelancerCard from '../components/FreelancerCard';
import { Button } from '../components/ui/button';
import { Heart, ArrowLeft, Loader2, Search } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoritesPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, [user, token]);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      if (user && token) {
        // Fetch from API for logged in users
        const response = await axios.get(`${API}/favorites/listings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(response.data);
      } else {
        // Fetch from localStorage for guests
        const localFavoriteIds = (() => { try { return JSON.parse(localStorage.getItem('favorites') || '[]'); } catch { return []; } })();
        if (localFavoriteIds.length > 0) {
          // Fetch listing details for each favorite
          const response = await axios.get(`${API}/listings`);
          const allListings = response.data;
          const favoritedListings = allListings.filter(l => localFavoriteIds.includes(l.id));
          setFavorites(favoritedListings);
        } else {
          setFavorites([]);
        }
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
      // Fallback to localStorage
      const localFavoriteIds = (() => { try { return JSON.parse(localStorage.getItem('favorites') || '[]'); } catch { return []; } })();
      if (localFavoriteIds.length > 0) {
        try {
          const response = await axios.get(`${API}/listings`);
          const allListings = response.data;
          const favoritedListings = allListings.filter(l => localFavoriteIds.includes(l.id));
          setFavorites(favoritedListings);
        } catch (e) {
          setFavorites([]);
        }
      } else {
        setFavorites([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (listingId) => {
    try {
      if (user && token) {
        await axios.post(`${API}/favorites/toggle`, 
          { listing_id: listingId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const localFavorites = (() => { try { return JSON.parse(localStorage.getItem('favorites') || '[]'); } catch { return []; } })();
        const newFavorites = localFavorites.filter(id => id !== listingId);
        localStorage.setItem('favorites', JSON.stringify(newFavorites));
      }
      setFavorites(favorites.filter(f => f.id !== listingId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[{ label: t('favorites.myFavorites'), href: null }]} />

      {/* Hero Section */}
      <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('favorites.myFavorites')}</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {t('favorites.listingsSaved', { count: favorites.length })}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-zinc-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-zinc-600" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">{t('favorites.noFavoritesYet')}</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              {t('favorites.noFavoritesDesc')}
            </p>
            <Button
              onClick={() => navigate('/freelancers')}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Search className="w-4 h-4 mr-2" />
              {t('favorites.exploreListings')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {favorites.map((listing) => (
              <div key={listing.id} className="relative group">
                <FreelancerCard freelancer={listing} />
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveFavorite(listing.id);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  title={t('favorites.removeFromFavorites')}
                >
                  <Heart className="w-4 h-4" fill="currentColor" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Info for guests */}
        {!user && favorites.length > 0 && (
          <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center">
            <p className="text-zinc-400 text-sm">
              💡 {t('favorites.syncFavorites')}
              <button
                onClick={() => navigate('/')}
                className="text-emerald-400 hover:text-emerald-300 ml-1 underline"
              >
                {t('favorites.signIn')}
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
